import React, { useCallback, useEffect, useState } from 'react'
import {
  ActivityIndicator,
  FlatList,
  Image,
  RefreshControl,
  TouchableOpacity,
  View,
} from 'react-native'
import { mutate } from 'swr'
import styled from 'styled-components'
import { observer } from 'mobx-react'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import Ionicons from 'react-native-vector-icons/Ionicons'
import { colors } from 'infra/color'
import { getSecureImageUrl, useSafeAreaInsets } from 'infra/util'
import { ColumnLayout, RowLayout } from 'ui/common/layout'
import { Body1, Caption, H2 } from 'ui/common/text'
import { PlaceCard } from 'ui/common/place-popover'
import { PROFILE_IMG_PLACEHOLDER_IMAGE } from 'image/icon'
import { useFeed } from 'api/hooks'
import { FeedItem } from 'infra/type'
import { FeedStackParamList } from 'navigation/feed-stack'
import { isValidPlace } from 'store/places'

const LIMIT = 10

type FeedScreenNavigationProp = StackNavigationProp<FeedStackParamList>

// createdAt(ISO) → "방금 전" / "3시간 전" / "2일 전" 형태의 상대 시간
const formatRelativeTime = (iso: string) => {
  const then = new Date(iso).getTime()
  if (isNaN(then)) return ''
  const sec = Math.floor((Date.now() - then) / 1000)
  if (sec < 60) return '방금 전'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}분 전`
  const hour = Math.floor(min / 60)
  if (hour < 24) return `${hour}시간 전`
  const day = Math.floor(hour / 24)
  if (day < 7) return `${day}일 전`
  if (day < 30) return `${Math.floor(day / 7)}주 전`
  if (day < 365) return `${Math.floor(day / 30)}개월 전`
  return `${Math.floor(day / 365)}년 전`
}

// contentType / objectType 조합으로 피드 행위 문구 생성
const feedActionLabel = (item: FeedItem) => {
  if (item.contentType === 'REPORT' && item.objectType === 'PLACE') {
    return '장소를 제보했어요'
  }
  return '새 소식'
}

const FeedItemCard = ({ item }: { item: FeedItem }) => {
  const navigation = useNavigation<FeedScreenNavigationProp>()
  const { user, place, createdAt } = item
  return (
    <View style={{ marginBottom: 20 }}>
      <TouchableOpacity
        activeOpacity={0.7}
        onPress={() =>
          navigation.navigate('UserDetailScreen', { userId: user.id })
        }
        style={{ paddingHorizontal: 20, marginBottom: 10 }}
      >
        <RowLayout style={{ alignItems: 'center' }}>
          <Image
            source={
              user.thumbnailImageUrl
                ? { uri: getSecureImageUrl(user.thumbnailImageUrl) }
                : PROFILE_IMG_PLACEHOLDER_IMAGE
            }
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              backgroundColor: colors.gray.v200,
              marginRight: 10,
            }}
          />
          <ColumnLayout style={{ flex: 1 }}>
            <Body1
              style={{ fontWeight: 'bold', color: colors.black, fontSize: 14 }}
            >
              {user.nickname}
            </Body1>
            <Caption style={{ color: colors.gray.v600, marginTop: 2 }}>
              {feedActionLabel(item)}
              {createdAt ? ` · ${formatRelativeTime(createdAt)}` : ''}
            </Caption>
          </ColumnLayout>
        </RowLayout>
      </TouchableOpacity>
      <View style={{ paddingHorizontal: 20 }}>
        <PlaceCard data={place} />
      </View>
    </View>
  )
}

export const FeedScreen = observer(() => {
  const insets = useSafeAreaInsets()

  const [items, setItems] = useState<FeedItem[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const { data, isLoading } = useFeed(LIMIT, cursor)

  useEffect(() => {
    if (Array.isArray(data?.items)) {
      const validItems = data.items.filter(
        (item) =>
          !!item?.user &&
          typeof item.user.id === 'number' &&
          typeof item.user.nickname === 'string' &&
          isValidPlace(item.place),
      )
      if (!cursor) {
        setItems(validItems)
      } else {
        setItems((prev) => [...prev, ...validItems])
      }
      setNextCursor(data.nextCursor)
      setIsLoadingMore(false)
    }
  }, [data, cursor])

  const loadMore = useCallback(() => {
    if (isLoadingMore || isLoading) return
    if (nextCursor) {
      setIsLoadingMore(true)
      setCursor(nextCursor)
    }
  }, [nextCursor, isLoadingMore, isLoading])

  // 당겨서 새로고침. 누적된 모든 /feeds 페이지 캐시를 비우고 커서를 초기화해
  // 첫 페이지를 새로 가져온다. (커서만 리셋하면 SWR 캐시 때문에 재요청이 일어나지 않는다)
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return
    setIsRefreshing(true)
    setNextCursor(null)
    setCursor(undefined)
    try {
      await mutate(
        (key) => typeof key === 'string' && key.startsWith('/feeds'),
        undefined,
        { revalidate: true },
      )
    } finally {
      setIsRefreshing(false)
    }
  }, [isRefreshing])

  const renderEmpty = () => {
    if (isLoading && items.length === 0) {
      return (
        <EmptyContainer>
          <ActivityIndicator size='large' color={colors.primary} />
        </EmptyContainer>
      )
    }
    return (
      <EmptyContainer>
        <Ionicons name='reader-outline' size={48} color={colors.gray.v300} />
        <Body1 style={{ color: colors.gray.v500, marginTop: 16 }}>
          아직 피드가 없어요
        </Body1>
      </EmptyContainer>
    )
  }

  return (
    <Container style={{ paddingTop: insets.top + 6 }}>
      <Header>
        <H2 style={{ fontWeight: 'bold', color: colors.black }}>피드</H2>
      </Header>
      <FlatList
        data={items}
        renderItem={({ item }) => <FeedItemCard item={item} />}
        keyExtractor={(item, index) => `${item.id}-${index}`}
        ListEmptyComponent={renderEmpty}
        ListFooterComponent={
          isLoadingMore ? (
            <ActivityIndicator
              size='small'
              color={colors.gray.v500}
              style={{ paddingVertical: 16 }}
            />
          ) : null
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.5}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
        style={{ flex: 1, backgroundColor: colors.gray.v100 }}
        contentContainerStyle={{
          paddingTop: 16,
          paddingBottom: 100,
          flexGrow: 1,
        }}
      />
    </Container>
  )
})

const Container = styled(ColumnLayout)`
  flex: 1;
  background-color: ${colors.white};
`

const Header = styled(RowLayout)`
  align-items: center;
  padding: 8px 20px 16px 20px;
  background-color: ${colors.white};
`

const EmptyContainer = styled(ColumnLayout)`
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background-color: ${colors.gray.v100};
`
